/**
 * Pure domain rules for the club membership model: membership-year resolution, fixed-period
 * term resolution, seat typing/validation, eligibility checks, and total price calculation.
 * No framework or database dependency. All date math is UTC-based so results are deterministic
 * regardless of server/client timezone; callers should pass calendar dates normalized to UTC
 * midnight (e.g. values read back from a PostgreSQL DATE column).
 */

import { calculateProcessingFee } from './processingFee';
import type { Region } from '../types/region';
import type {
  Club,
  Member,
  Membership,
  MembershipAddon,
  MembershipSeatType,
  MembershipStatus,
  MembershipType,
} from '../types/organization';

const ONE_DAY_MS = 24 * 60 * 60 * 1000;

function addUTCMonths(date: Date, months: number): Date {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();
  const day = date.getUTCDate();
  const targetMonthIndex = month + months;
  // Day 0 of the month after the target month = last day of the target month, for clamping
  // overflow (e.g. Jan 31 + 1 month -> Feb 28/29, matching common billing-period semantics).
  const daysInTargetMonth = new Date(Date.UTC(year, targetMonthIndex + 1, 0)).getUTCDate();
  const clampedDay = Math.min(day, daysInTargetMonth);
  return new Date(Date.UTC(year, targetMonthIndex, clampedDay));
}

function subtractUTCDays(date: Date, days: number): Date {
  return new Date(date.getTime() - days * ONE_DAY_MS);
}

function calculateAgeInYears(dateOfBirth: Date, asOfDate: Date): number {
  let age = asOfDate.getUTCFullYear() - dateOfBirth.getUTCFullYear();
  const asOfMonthDay = asOfDate.getUTCMonth() * 100 + asOfDate.getUTCDate();
  const dobMonthDay = dateOfBirth.getUTCMonth() * 100 + dateOfBirth.getUTCDate();
  if (asOfMonthDay < dobMonthDay) age -= 1;
  return age;
}

export interface MembershipYear {
  startDate: Date;
  endDate: Date;
  seasonLabel: string;
}

/**
 * The club membership year (season) containing `asOf`, e.g. "MEMBERSHIP 2025/26".
 * Season-anchored plans (Adult, Couple, Family) all expire at `endDate` regardless of join date.
 */
export function resolveMembershipYear(
  club: Pick<Club, 'membershipYearStartMonth' | 'membershipYearStartDay'>,
  asOf: Date
): MembershipYear {
  const asOfYear = asOf.getUTCFullYear();
  const startMonthIndex = club.membershipYearStartMonth - 1;

  const candidateStart = new Date(Date.UTC(asOfYear, startMonthIndex, club.membershipYearStartDay));
  const startYear = asOf.getTime() >= candidateStart.getTime() ? asOfYear : asOfYear - 1;

  const startDate = new Date(Date.UTC(startYear, startMonthIndex, club.membershipYearStartDay));
  const nextStartDate = new Date(Date.UTC(startYear + 1, startMonthIndex, club.membershipYearStartDay));
  const endDate = subtractUTCDays(nextStartDate, 1);

  const endYear = endDate.getUTCFullYear();
  const seasonLabel = startYear === endYear ? `${startYear}` : `${startYear}/${String(endYear).slice(-2)}`;

  return { startDate, endDate, seasonLabel };
}

export interface MembershipPeriod {
  startDate: Date;
  /** Null for recurring (Stripe-subscription-managed) plans. */
  endDate: Date | null;
  /** Set only for season-anchored fixed-period plans. */
  seasonLabel: string | null;
}

/**
 * Resolves the start/end of one membership term.
 * - `recurring`: open-ended; Stripe subscription drives renewal.
 * - `fixed_period` + `season`: runs from `joinDate` to the containing membership year's end,
 *   regardless of when in the season the member joined.
 * - `fixed_period` + `join_date`: runs `periodMonths` from `joinDate` (e.g. "3 Months", "6 Months").
 */
export function resolveMembershipPeriod(
  plan: Pick<MembershipType, 'termType' | 'periodAnchor' | 'periodMonths'>,
  joinDate: Date,
  membershipYear: MembershipYear | null
): MembershipPeriod {
  if (plan.termType === 'recurring') {
    return { startDate: joinDate, endDate: null, seasonLabel: null };
  }

  if (plan.periodAnchor === 'season') {
    if (!membershipYear) {
      throw new Error('resolveMembershipPeriod: a season-anchored plan requires the club membership year');
    }
    return { startDate: joinDate, endDate: membershipYear.endDate, seasonLabel: membershipYear.seasonLabel };
  }

  if (plan.periodAnchor === 'join_date') {
    if (!plan.periodMonths) {
      throw new Error('resolveMembershipPeriod: a join-date-anchored plan requires periodMonths');
    }
    return {
      startDate: joinDate,
      endDate: subtractUTCDays(addUTCMonths(joinDate, plan.periodMonths), 1),
      seasonLabel: null,
    };
  }

  throw new Error('resolveMembershipPeriod: a fixed_period plan requires a periodAnchor');
}

function toUtcCalendarDate(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

/**
 * End date to persist when a membership is cancelled (operator, member self-service, or
 * rejected verification). Recurring plans have no stored end; early cancel of a fixed
 * period uses the cancellation day. A period that already ended is left unchanged.
 */
export function resolveMembershipEndDateOnCancel(
  existingEndDate: Date | string | null | undefined,
  cancelledAt: Date
): Date {
  const cancelledDay = toUtcCalendarDate(cancelledAt);
  if (!existingEndDate) return cancelledDay;
  const existingDay = toUtcCalendarDate(new Date(existingEndDate));
  return existingDay.getTime() < cancelledDay.getTime() ? existingDay : cancelledDay;
}

/**
 * Date shown as the membership period end. Cancelled recurring rows historically left
 * `endDate` null — fall back to `updatedAt` so the list does not show "Ongoing".
 */
export function resolveDisplayedMembershipEndDate(
  membership: Pick<Membership, 'endDate' | 'status' | 'updatedAt'>
): Date | null {
  if (membership.endDate) return new Date(membership.endDate);
  if (membership.status === 'cancelled' && membership.updatedAt) {
    return new Date(membership.updatedAt);
  }
  return null;
}

/**
 * Adult or junior seat type for a covered person, from date of birth. Members with no recorded
 * date of birth are treated as adults (the conservative default: never under-charge a plan by
 * assuming a junior seat that hasn't been confirmed).
 */
export function resolveSeatType(
  member: Pick<Member, 'dateOfBirth'>,
  asOfDate: Date,
  juniorMaxAge: number
): Exclude<MembershipSeatType, 'associate'> {
  if (!member.dateOfBirth) return 'adult';
  return calculateAgeInYears(member.dateOfBirth, asOfDate) <= juniorMaxAge ? 'junior' : 'adult';
}

export interface MembershipSeatValidationResult {
  valid: boolean;
  errors: string[];
}

/**
 * Enforces a plan's seat composition: adult cap, included juniors (unless extras are free),
 * and the overall seat cap.
 */
export function validateMembershipSeats(
  plan: Pick<MembershipType, 'maxAdults' | 'includedJuniors' | 'extraJuniorsFree' | 'maxTotalSeats'>,
  seats: Array<{ seatType: MembershipSeatType }>
): MembershipSeatValidationResult {
  const errors: string[] = [];
  const adults = seats.filter((s) => s.seatType === 'adult').length;
  const juniors = seats.filter((s) => s.seatType === 'junior').length;

  if (seats.length === 0) {
    errors.push('At least one person must be covered by this membership.');
  }

  if (adults > plan.maxAdults) {
    errors.push(
      plan.maxAdults === 0
        ? 'This plan does not cover any adult seats.'
        : `This plan covers up to ${plan.maxAdults} adult${plan.maxAdults === 1 ? '' : 's'}, but ${adults} were selected.`
    );
  }

  if (!plan.extraJuniorsFree && juniors > plan.includedJuniors) {
    errors.push(
      `This plan includes ${plan.includedJuniors} junior${plan.includedJuniors === 1 ? '' : 's'}, but ${juniors} were selected.`
    );
  }

  if (plan.maxTotalSeats != null && seats.length > plan.maxTotalSeats) {
    errors.push(`This plan covers up to ${plan.maxTotalSeats} people in total, but ${seats.length} were selected.`);
  }

  return { valid: errors.length === 0, errors };
}

export interface MembershipEligibilityContext {
  /** True once proof (e.g. a concession card) has been sighted/uploaded for this join or renewal. */
  proofProvided?: boolean;
  /** True when the joining member is linked to an active junior member at the club (Parent plans). */
  hasLinkedJunior?: boolean;
}

export interface MembershipEligibilityResult {
  /** False only for unmet hard constraints (age). Purchase must be blocked. */
  eligible: boolean;
  /** True when requiresProof or requiresLinkedJunior is unmet; purchase may proceed to pending_verification. */
  requiresVerification: boolean;
  errors: string[];
  verificationReasons: string[];
}

/**
 * Age-at-join/renewal eligibility (hard block) plus proof/linked-junior requirements, which are
 * soft: they don't block purchase but flag the resulting membership for staff verification
 * (`pending_verification`) instead of activating it immediately.
 */
export function checkEligibility(
  plan: Pick<MembershipType, 'minAgeAtJoin' | 'maxAgeAtJoin' | 'requiresProof' | 'proofLabel' | 'requiresLinkedJunior'>,
  member: Pick<Member, 'dateOfBirth'>,
  asOfDate: Date,
  context: MembershipEligibilityContext = {}
): MembershipEligibilityResult {
  const errors: string[] = [];
  const verificationReasons: string[] = [];

  if (plan.minAgeAtJoin != null || plan.maxAgeAtJoin != null) {
    if (!member.dateOfBirth) {
      errors.push('Date of birth is required to confirm eligibility for this plan.');
    } else {
      const age = calculateAgeInYears(member.dateOfBirth, asOfDate);
      if (plan.minAgeAtJoin != null && age < plan.minAgeAtJoin) {
        errors.push(`Must be at least ${plan.minAgeAtJoin} years old to join this plan.`);
      }
      if (plan.maxAgeAtJoin != null && age > plan.maxAgeAtJoin) {
        errors.push(`Must be ${plan.maxAgeAtJoin} or under to join this plan.`);
      }
    }
  }

  if (plan.requiresProof && !context.proofProvided) {
    verificationReasons.push(`${plan.proofLabel?.trim() || 'Proof'} required before this membership can be activated.`);
  }
  if (plan.requiresLinkedJunior && !context.hasLinkedJunior) {
    verificationReasons.push('Must be linked to a junior member before this membership can be activated.');
  }

  return {
    eligible: errors.length === 0,
    requiresVerification: verificationReasons.length > 0,
    errors,
    verificationReasons,
  };
}

export interface MembershipAddonSelectionInput {
  addon: Pick<MembershipAddon, 'priceCents' | 'concessionPriceCents' | 'pricingMode'>;
  /** Defaults to the number of adult seats for `per_adult` add-ons, or 1 for `per_membership`. */
  quantity?: number;
}

export interface ResolvedMembershipAddonSelection {
  unitPriceCents: number;
  quantity: number;
  totalCents: number;
}

export interface MembershipTotalInput {
  plan: Pick<MembershipType, 'priceCents' | 'requiresProof'>;
  seats: Array<{ seatType: MembershipSeatType }>;
  addonSelections?: MembershipAddonSelectionInput[];
  /** Only apply when the club has surcharging enabled for this purchase (checked by the caller). */
  applySurcharge?: boolean;
  region?: Region | null;
}

export interface MembershipTotalBreakdown {
  basePriceCents: number;
  addonsTotalCents: number;
  surchargeCents: number;
  totalCents: number;
  /** Same order as `addonSelections`, with quantity/unit price resolved for persistence. */
  resolvedAddonSelections: ResolvedMembershipAddonSelection[];
}

/**
 * Base plan price + per-adult/per-membership add-on expansion (using concession pricing when the
 * plan itself requires proof, e.g. a Concession plan) + surcharge via the existing, Stripe-rate-
 * accurate `calculateProcessingFee`. Amounts are in cents throughout except where
 * `calculateProcessingFee` operates in dollars.
 */
export function calculateMembershipTotal(input: MembershipTotalInput): MembershipTotalBreakdown {
  const { plan, seats, addonSelections = [], applySurcharge = false, region = null } = input;
  const adultSeatCount = seats.filter((s) => s.seatType === 'adult').length;

  const resolvedAddonSelections: ResolvedMembershipAddonSelection[] = addonSelections.map(({ addon, quantity }) => {
    const unitPriceCents =
      plan.requiresProof && addon.concessionPriceCents != null ? addon.concessionPriceCents : addon.priceCents;
    const resolvedQuantity = quantity ?? (addon.pricingMode === 'per_adult' ? adultSeatCount : 1);
    return {
      unitPriceCents,
      quantity: resolvedQuantity,
      totalCents: unitPriceCents * resolvedQuantity,
    };
  });

  const basePriceCents = plan.priceCents;
  const addonsTotalCents = resolvedAddonSelections.reduce((sum, selection) => sum + selection.totalCents, 0);
  const subtotalCents = basePriceCents + addonsTotalCents;

  let surchargeCents = 0;
  if (applySurcharge && region) {
    const surchargeDollars = calculateProcessingFee(subtotalCents / 100, region);
    surchargeCents = Math.round(surchargeDollars * 100);
  }

  return {
    basePriceCents,
    addonsTotalCents,
    surchargeCents,
    totalCents: subtotalCents + surchargeCents,
    resolvedAddonSelections,
  };
}

/** One covering seat used to decide what membership status to show on a member roster row. */
export interface MembershipStatusCandidate {
  status: MembershipStatus;
  isOccupying: boolean;
  createdAt: Date;
}

const OCCUPYING_MEMBERSHIP_STATUS_RANK: Record<MembershipStatus, number> = {
  active: 0,
  pending_verification: 1,
  past_due: 2,
  pending_payment: 3,
  expired: 4,
  cancelled: 5,
};

/**
 * Membership status shown on the operator Members list. Occupying seats win (current
 * entitlement). If none occupy, the latest membership is used so Expired/Cancelled still show
 * instead of "None".
 */
export function pickDisplayedMembershipStatus(
  candidates: MembershipStatusCandidate[]
): MembershipStatus | null {
  if (candidates.length === 0) return null;

  const occupying = candidates.filter((candidate) => candidate.isOccupying);
  if (occupying.length > 0) {
    return [...occupying].sort((a, b) => {
      const rank =
        OCCUPYING_MEMBERSHIP_STATUS_RANK[a.status] - OCCUPYING_MEMBERSHIP_STATUS_RANK[b.status];
      if (rank !== 0) return rank;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })[0].status;
  }

  return [...candidates].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  )[0].status;
}
